/**
 * Global Reusable Functions for RS Camp Pages
 */
window.RsCampPage = window.RsCampPage || {};

window.RsCampPage.setCookie = function(formData) {
  const cookieName = 'rs_camp_form_data';
  const cookieValue = JSON.stringify(formData);
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + 7);

  document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
};

window.RsCampPage.getCookie = function() {
  const cookieName = 'rs_camp_form_data';
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch (error) {
        console.error('Error parsing form data cookie:', error);
        return null;
      }
    }
  }
  return null;
};

window.RsCampPage.clearCookie = function() {
  const cookieName = 'rs_camp_form_data';
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
};

window.RsCampPage.formatHeightForDisplay = function(height) {
  if (!height || typeof height !== 'string') return height || '';
  const match = height.replace(/\s/g, '').match(/^(\d+)x(\d+)$/i);
  return match ? `${match[1]}' ${match[2]}"` : height;
};

window.RsCampPage.getCampData = function() {
  if (window.RsCampPage._campData) {
    return window.RsCampPage._campData;
  }

  const campDataScript = document.querySelector('script[data-camp-page-data]');
  if (campDataScript) {
    try {
      window.RsCampPage._campData = JSON.parse(campDataScript.textContent);
      return window.RsCampPage._campData;
    } catch (error) {
      console.error('Error parsing camp data:', error);
      return null;
    }
  }
  
  return null;
};

/**
 * Run callback after layout is ready (Safari scroll fix). Uses setTimeout to defer past paint.
 */
function runAfterNextPaint(callback) {
  setTimeout(callback, 0);
}

/**
 * Camp page state for navigation persistence (back button from PDP).
 * Stored in history.state - no URL changes.
 */
const CAMP_SCROLL_KEY = 'rs_camp_scroll_position';
const CAMP_STATE_KEY = 'campState';
/** 
 * Camp PDP context:
 * Stored in sessionStorage.
 */
const CAMP_PDP_CONTEXT_KEY = 'rs_camp_pdp_context';

window.RsCampPage.getCampStateFromHistory = function() {
  const state = history.state?.[CAMP_STATE_KEY];
  if (!state) return { category: null, page: null, sortBy: null, filterParams: '' };
  return {
    category: state.category ?? null,
    page: state.page ?? null,
    sortBy: state.sortBy ?? null,
    filterParams: state.filterParams ?? ''
  };
};

window.RsCampPage.updateCampStateInHistory = function(state) {
  const current = window.RsCampPage.getCampStateFromHistory();
  const merged = {
    category: state.category !== undefined ? state.category : current.category,
    page: state.page !== undefined ? state.page : current.page,
    sortBy: state.sortBy !== undefined ? state.sortBy : current.sortBy,
    filterParams: state.filterParams !== undefined ? state.filterParams : current.filterParams
  };
  const newState = { ...(history.state || {}), [CAMP_STATE_KEY]: merged };
  history.replaceState(newState, '', location.href);
};

window.RsCampPage.saveCampScrollPosition = function() {
  try {
    sessionStorage.setItem(CAMP_SCROLL_KEY, String(window.scrollY));
  } catch (e) {}
};

window.RsCampPage.restoreCampScrollPosition = function() {
  try {
    const saved = sessionStorage.getItem(CAMP_SCROLL_KEY);
    if (saved != null) {
      sessionStorage.removeItem(CAMP_SCROLL_KEY);
      const y = parseInt(saved, 10);
      if (!isNaN(y)) {
        runAfterNextPaint(() => window.scrollTo(0, y));
      }
    }
  } catch (e) {}
};

// Restore scroll when Safari restores page from bfcache (back button)
window.addEventListener('pageshow', function(e) {
  if (e.persisted) {
    window.RsCampPage.restoreCampScrollPosition();
  }
});

/**
 * Add line item properties to a single form.
 * @param {HTMLFormElement} form - The form element
 * @param {Object} options - Either { campCategory } (fetches rest from cookie/campData) or explicit { campTitle, campCategory, camperName, camperAge, camperHeight, camperWeight, campNameLabel? }
 */
window.RsCampPage.addLineItemPropertiesToForm = function(form, options) {
  if (form.querySelector('input[name="properties[_CampTitle]"]')) return;

  let props;
  if (options.campTitle !== undefined) {
    props = options;
  } else {
    const campData = window.RsCampPage.getCampData();
    const formData = window.RsCampPage.getCookie();
    if (!campData || !formData || !options.campCategory) return;
    props = {
      campTitle: campData.camp_name,
      campCategory: options.campCategory,
      camperName: formData.camperName,
      camperAge: formData.camperAge,
      camperHeight: formData.camperHeight,
      camperWeight: formData.camperWeight,
      campNameLabel: formData.campNameLabel
    };
  }

  const inputs = [
    { name: 'properties[_CampTitle]', value: props.campTitle },
    { name: 'properties[_CampCategory]', value: props.campCategory },
    { name: 'properties[_CamperName]', value: props.camperName },
    { name: 'properties[_CamperAge]', value: props.camperAge },
    { name: 'properties[_CamperHeight]', value: props.camperHeight },
    { name: 'properties[_CamperWeight]', value: props.camperWeight }
  ];
  if (props.campNameLabel?.trim()) {
    inputs.push({ name: 'properties[_CampNameLabel]', value: props.campNameLabel });
  }

  inputs.forEach(({ name, value }) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value || '';
    form.appendChild(input);
  });
};

/**
 * Add line item properties data attributes to quick-add buttons.
 * @param {HTMLElement} container - Container to search for [data-quick-add-btn]
 * @param {string} campCategory - Category for _CampCategory
 */
window.RsCampPage.addLineItemPropertiesToQuickAddButtons = function(container, campCategory) {
  const campData = window.RsCampPage.getCampData();
  const formData = window.RsCampPage.getCookie();
  if (!campData || !formData) return;

  container.querySelectorAll('[data-quick-add-btn]').forEach(button => {
    button.setAttribute('data-camp-title', campData.camp_name);
    button.setAttribute('data-camp-category', campCategory);
    button.setAttribute('data-camper-name', formData.camperName);
    button.setAttribute('data-camper-age', formData.camperAge);
    button.setAttribute('data-camper-height', formData.camperHeight);
    button.setAttribute('data-camper-weight', formData.camperWeight);
    if (formData.campNameLabel?.trim()) {
      button.setAttribute('data-camp-name-label', formData.campNameLabel);
    }
  });
};

/**
 * Add line item properties to all forms and quick-add buttons in a container.
 * @param {HTMLElement} container - Container element
 * @param {string} campCategory - Category for _CampCategory (itemTitle or blockTitle)
 */
window.RsCampPage.addLineItemPropertiesToContainer = function(container, campCategory) {
  if (!campCategory) return;

  container.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
    window.RsCampPage.addLineItemPropertiesToForm(form, { campCategory });
  });
  window.RsCampPage.addLineItemPropertiesToQuickAddButtons(container, campCategory);
};

/**
 * Initialize line item properties for dynamically loaded products (pagination, filter, sort).
 * Reads campCategory from container's data-item-title attribute.
 */
window.RsCampPage.initializeDynamicProductsForContainer = function(container) {
  const campCategory = container.getAttribute('data-item-title');
  window.RsCampPage.addLineItemPropertiesToContainer(container, campCategory);
};

/**
 * When the shopper opens a product from camp results, persist line-item context for the PDP form.
 * Scoped to camp product areas so other `[data-item-title]` nodes (e.g. cart) do not apply.
 */
window.RsCampPage.storeCampContextForProductUrl = function(link) {
  const categoryHost = link?.closest(
    '.camp-result__content-products[data-item-title], .camp-result__content-featured-products[data-item-title]'
  );
  const campCategory = categoryHost?.getAttribute('data-item-title') || '';
  const href = link?.getAttribute('href') || '';
  const match = href.match(/\/products\/([^/?#]+)/);
  const productHandle = match ? decodeURIComponent(match[1]) : null;

  const campData = window.RsCampPage.getCampData();
  const formData = window.RsCampPage.getCookie();

  try {
    if (!productHandle || !campData || !formData || !campCategory) {
      sessionStorage.removeItem(CAMP_PDP_CONTEXT_KEY);
      return;
    }

    sessionStorage.setItem(
      CAMP_PDP_CONTEXT_KEY,
      JSON.stringify({
        productHandle,
        campTitle: campData.camp_name,
        campCategory,
        camperName: formData.camperName,
        camperAge: formData.camperAge,
        camperHeight: formData.camperHeight,
        camperWeight: formData.camperWeight,
        campNameLabel: formData.campNameLabel || ''
      })
    );
  } catch (e) {}
};

/**
 * On product pages: if session context matches this product, add the same hidden property inputs as on the camp grid.
 */
window.RsCampPage.applyStoredCampLineItemPropertiesToPdp = function() {
  const component = document.querySelector('product-component[data-product-handle]');
  if (!component) return;

  let storedCampPdpContext;
  try {
    const raw = sessionStorage.getItem(CAMP_PDP_CONTEXT_KEY);
    if (!raw) return;
    storedCampPdpContext = JSON.parse(raw);
  } catch (e) {
    return;
  }

  if (
    !storedCampPdpContext?.productHandle ||
    storedCampPdpContext.productHandle !== component.dataset.productHandle
  ) {
    return;
  }

  const options = {
    campTitle: storedCampPdpContext.campTitle,
    campCategory: storedCampPdpContext.campCategory,
    camperName: storedCampPdpContext.camperName,
    campNameLabel: storedCampPdpContext.campNameLabel || '',
    camperAge: storedCampPdpContext.camperAge,
    camperHeight: storedCampPdpContext.camperHeight,
    camperWeight: storedCampPdpContext.camperWeight
  };

  if (!options.campTitle || !options.campCategory || !options.camperName) return;

  document.querySelectorAll('product-component form[data-product-form]').forEach((form) => {
    window.RsCampPage.addLineItemPropertiesToForm(form, options);
  });
};

window.RsCampPage.scrollToResult = function() {
  const resultContainer = document.querySelector('rs-camp-result');
  if (!resultContainer) return;

  const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 120;
  const elementTop = resultContainer.getBoundingClientRect().top + window.pageYOffset;
  const offsetPosition = elementTop - headerHeight - 20;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

/**
 * Global function to load products using Shopify Section Rendering API
 * @param {string} collectionUrl - The collection URL
 * @param {string} filterParams - Filter query parameters
 * @param {HTMLElement} container - Container element to inject HTML
 * @param {boolean} enableFiltering - Whether filtering is enabled
 */
window.RsCampPage.loadProducts = async function(collectionUrl, filterParams = '', container, enableFiltering = false, enableDepartmentFiltering = false) {
  container.classList.add('is-loading');
  
  try {
    const campData = window.RsCampPage.getCampData();
    const sectionId = campData.collection_section;
    
    let url = `${collectionUrl}?section_id=${sectionId}`;
    
    /**
     * APPLY DEPARTMENT FILTER BY DEFAULT WHEN
     *  - Filtering and department filtering are enabled
     *  - The department filter is not already in the URL
     */
    const formData = window.RsCampPage.getCookie();
    if (formData && formData.campDepartment && enableFiltering && enableDepartmentFiltering) {
      if (!url.includes('filter.p.m.custom.dept=') && !filterParams.includes('filter.p.m.custom.dept=')) {
        const departmentFilter = `filter.p.m.custom.dept=${formData.campDepartment}`;
        url += `&${departmentFilter}`;
      }
    }
    
    if (filterParams) {
      url += `&${filterParams}`;
    }
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const html = await response.text();
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const sectionContent = tempDiv.querySelector('#shopify-section-rs-camp-page-collection');
    if (sectionContent) {
      if (enableFiltering) {
        container.innerHTML = sectionContent.innerHTML; // Both filters and products
      } else {
        const productsElement = sectionContent.querySelector('rs-camp-result-products');
        container.innerHTML = productsElement.outerHTML; // Only products
      }

      // Initialize line item properties for dynamically loaded products (pagination, filter, sort)
      window.RsCampPage.initializeDynamicProductsForContainer(container);
    }

  } catch (error) {
    console.error('Error loading products:', error);
    container.innerHTML = '<p>Error loading products. Please try again.</p>';
  } finally {
    container.classList.remove('is-loading');
  }
};


/**
 * RS Camp Form. Handles form submission:
 *  - Cookie storage using HTML5 validation
 *  - Show/hide form and result sections based on form submission
 */
class RSCampForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-camp-form]');
    this.boundSubmitHandler = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    if (this.form) {
      this.form.addEventListener('submit', this.boundSubmitHandler);
    }
    this.initializePage();
  }

  /**
   * On page load - check for existing cookie and show appropriate step
   */
  initializePage() {
    const existingData = window.RsCampPage.getCookie();
    if (existingData) {
      this.toggleSections(true);
      this.dispatchFormEvent(existingData);
    } else {
      this.toggleSections(false);
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.boundSubmitHandler);
    }
  }

  /**
   * Handle form submission
   */
  handleSubmit(event) {
    event.preventDefault();
    
    if (this.form.checkValidity()) {
      const formData = this.collectFormData();
      window.RsCampPage.setCookie(formData);
      this.toggleSections(true);
      this.dispatchFormEvent(formData);
    } else {
      this.form.reportValidity();
    }
  }

  /**
   * Collect form data from FormData API
   */
  collectFormData() {
    const formData = new FormData(this.form);
    const feet = formData.get('camper-height-feet') || '';
    const inches = formData.get('camper-height-inches') || '';
    const camperHeight = (feet && inches) ? `${feet} x ${inches}` : '';

    return {
      camperName: formData.get('camper-name') || '',
      campNameLabel: formData.get('camper-name-label') || '',
      camperAge: parseInt(formData.get('camper-age')) || 0,
      camperHeight,
      camperWeight: parseInt(formData.get('camper-weight')) || 0,
      campDepartment: formData.get('camp-department') || '',
      timestamp: new Date().toISOString()
    };
  }


  /**
   * Toggle between form and result sections
   */
  toggleSections(showResult = true) {
    const formWrapper = this.closest('rs-camp-form-wrapper');
    const resultWrapper = document.querySelector('rs-camp-result-wrapper');
    const mainContent = document.querySelector('#MainContent');
    
    // Toggle form and result sections
    if (formWrapper && resultWrapper) {
      if (showResult) {
        formWrapper.setAttribute('hidden', '');
        resultWrapper.removeAttribute('hidden');
      } else {
        formWrapper.removeAttribute('hidden');
        resultWrapper.setAttribute('hidden', '');
      }
    }

    // Toggle sections based on class lists from section settings
    if (mainContent) {
      const campData = window.RsCampPage.getCampData();
      const afterFormClasses = campData?.sections_to_show_after_form || [];
      const afterResultClasses = campData?.sections_to_show_after_result || [];

      mainContent.querySelectorAll('.shopify-section').forEach(section => {
        if (section.classList.contains('shopify-section--camp-page')) return;

        const matchesForm = afterFormClasses.some(cls => cls && section.querySelector(`.${cls.trim()}`));
        const matchesResult = afterResultClasses.some(cls => cls && section.querySelector(`.${cls.trim()}`));

        // Sections not in either list are left untouched (always visible)
        if (matchesForm) {
          section.toggleAttribute('hidden', showResult);
        } else if (matchesResult) {
          section.toggleAttribute('hidden', !showResult);
        }
      });
    }
  }

  /**
   * Dispatch form submitted event
   */
  dispatchFormEvent(formData) {
    document.dispatchEvent(new CustomEvent('rs:camp-form:submitted', {
      bubbles: true,
      detail: { formData }
    }));
  }
}


/**
 * RS Camp Result. Handles result section:
 *  - Generates sidebar and content based on customization settings
 *  - Handles sidebar navigation and content display
 */
class RSCampResult extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.boundHandleFormSubmitted = this.handleFormSubmitted.bind(this);
    this.boundHandleQuickAddModalCreated = this.handleQuickAddModalCreated.bind(this);
    
    document.addEventListener('rs:camp-form:submitted', this.boundHandleFormSubmitted);
    document.addEventListener('theme:quick-add:modal-created', this.boundHandleQuickAddModalCreated);
    this.initializeResult();
  }

  disconnectedCallback() {
    if (this.boundHandleFormSubmitted) {
      document.removeEventListener('rs:camp-form:submitted', this.boundHandleFormSubmitted);
    }
    if (this.boundHandleQuickAddModalCreated) {
      document.removeEventListener('theme:quick-add:modal-created', this.boundHandleQuickAddModalCreated);
    }
  }

  /**
   * Initialize result section
   */
  initializeResult() {
    const formData = window.RsCampPage.getCookie();
    if (formData) {
      this.handleFormSubmitted();
    }
  }

  /**
   * Handle form submitted event and build sidebar
   */
  handleFormSubmitted() {
    const campData = window.RsCampPage.getCampData();
    if (!campData || !campData.checklist_blocks) return;

    const accordionContainer = this.querySelector('[data-camp-result-accordion]');
    const dropdownContainer = this.querySelector('[data-camp-result-dropdown] [data-grid-slider]');
    const dropdownWrapper = this.querySelector('[data-camp-result-dropdown]');
    
    if (accordionContainer) {
      accordionContainer.innerHTML = this.generateAccordionHTML();
      
      this.querySelectorAll('.camp-result__nav-button').forEach(button => {
        button.addEventListener('click', async (e) => {
          if (accordionContainer && accordionContainer.classList.contains('is-disabled')) {
            return;
          }
          
          // Prevent duplicate requests if button is already active
          if (e.target.classList.contains('is-active')) {
            return;
          }
          
          accordionContainer.classList.add('is-disabled');
          
          try {
            const index = parseInt(e.target.getAttribute('data-item-index'));
            window.RsCampPage.scrollToResult();
            await this.showContentByIndex(index);           
          } finally {
            accordionContainer.classList.remove('is-disabled');
          }
        });
      });
    }

    if (dropdownContainer) {
      dropdownContainer.innerHTML = this.generateDropdownHTML();
      
        requestAnimationFrame(() => {
          const gridSlider = this.querySelector('.camp-result__dropdown-slider');
          if (gridSlider) {
            window.dispatchEvent(new Event('resize'));
            
            if (typeof gridSlider.init === 'function') {
              gridSlider.init();
            }
            
            // Add event listener for slider arrow clicks to close dropdowns
            this.addSliderArrowListeners(gridSlider);
          }
        });

        this.querySelectorAll('.camp-result__dropdown-item button[data-item-index]').forEach(button => {
          button.addEventListener('click', async (e) => {
            if (dropdownWrapper && dropdownWrapper.classList.contains('is-disabled')) {
              return;
            }
            
            // Prevent duplicate requests if the specific button is already active
            if (e.target.classList.contains('is-active')) {
              // Still close dropdown and scroll for UX, but don't fetch content
              const dropdownItem = e.target.closest('.camp-result__dropdown-item');
              if (dropdownItem && dropdownItem.getAttribute('data-content-type') === 'products') {
                const details = e.target.closest('details');
                if (details) {
                  details.removeAttribute('open');
                }
              }
              this.scrollToContentOnMobile();
              return;
            }
            
            dropdownWrapper.classList.add('is-disabled');
            
            try {
              const index = parseInt(e.target.getAttribute('data-item-index'));
              // Scroll BEFORE loading: Safari's scroll anchoring overrides scrollTo when content is replaced.
              // By scrolling first, we're at the right position before innerHTML changes.
              this.scrollToContentOnMobile();
              await this.showContentByIndex(index);
              
              const dropdownItem = e.target.closest('.camp-result__dropdown-item');
              if (dropdownItem && dropdownItem.getAttribute('data-content-type') === 'products') {
                const details = e.target.closest('details');
                if (details) {
                  details.removeAttribute('open');
                }
              }
            } finally {
              dropdownWrapper.classList.remove('is-disabled');
            }
          });
        });
    }
    
    // Restore state from history (back from PDP) or show first item
    const savedState = window.RsCampPage.getCampStateFromHistory();
    const initialIndex = (savedState.category && savedState.category > 0) ? savedState.category : 1;

    if (savedState.filterParams || savedState.page || savedState.sortBy) {
      const parts = [];
      if (savedState.filterParams) parts.push(savedState.filterParams);
      if (savedState.sortBy) parts.push(`sort_by=${savedState.sortBy}`);
      if (savedState.page && savedState.page > 1) parts.push(`page=${savedState.page}`);
      this.pendingFilterParams = parts.join('&');
    }

    this.showContentByIndex(initialIndex).then(() => {
      window.RsCampPage.restoreCampScrollPosition();
    });

    // Show shopper info
    this.showShopperInfo();

    // Save scroll when navigating to product pages
    this.setupNavigationPersistence();
  }

  /**
   * Show shopper info with reset functionality
   */
  showShopperInfo() {
    const shopperInfoContainer = document.querySelector('rs-camp-shopper-info');
    if (!shopperInfoContainer) return;

    const formData = window.RsCampPage.getCookie();
    const campData = window.RsCampPage.getCampData();
    if (!formData) return;

    shopperInfoContainer.innerHTML = `
      <div class="camp-result__shopper--title h3 heading-small">${campData.camp_name}</div>
      <div class="camp-result__shopper--message">
        <div class="camp-result__shopper--message-name">
          You're currently shopping for <strong>${formData.camperName}</strong>
        </div>
        <div class="camp-result__shopper--message-info">
          <span><strong>${formData.camperAge}</strong> ${formData.camperAge === 1 ? 'year' : 'years'} old</span>
          <span><strong>${window.RsCampPage.formatHeightForDisplay(formData.camperHeight)}</strong> tall</span>
          <span><strong>${formData.camperWeight}</strong> lbs</span>
        </div>
      </div>
      <div class="camp-result__shopper--info">
        Note: Changing the camper will clear your current camper details but will not affect your cart.
      </div>
      <div class="camp-result__shopper--button">
        <button type="button" class="btn btn--small btn--outline btn--secondary" data-shopper-info-reset>
          Change to another camper
        </button>
      </div> 
    `;
    
    // Add reset functionality
    shopperInfoContainer.querySelector('[data-shopper-info-reset]').addEventListener('click', () => {
      window.RsCampPage.clearCookie();
      window.RsCampPage.updateCampStateInHistory({ category: null, page: null, sortBy: null, filterParams: '' });
      window.location.reload();
    });
  }

  /**
   * Show content based on item index
   */
  async showContentByIndex(index) {
    const button = this.querySelector(`[data-item-index="${index}"]`);
    const contentContainer = this.querySelector('[data-camp-result-content]');
    if (!button || !contentContainer) return;

    // Update current index and active state
    this.currentIndex = index;
    if (!this.pendingFilterParams) {
      window.RsCampPage.updateCampStateInHistory({ category: index, page: 1, sortBy: null, filterParams: '' });
    }
    
    // Update active state for desktop accordion buttons
    this.querySelectorAll('.camp-result__nav-button').forEach(btn => btn.classList.remove('is-active'));
    button.classList.add('is-active');
    
    // Update active state for mobile dropdown items
    this.resetDropdownTitles();
    
    this.querySelectorAll('.camp-result__dropdown-item').forEach(item => {
      item.classList.remove('is-active');
      const contentType = item.getAttribute('data-content-type');
      
      if (contentType === 'text' || contentType === 'featured_products') {
        const button = item.querySelector('button[data-item-index]');
        if (button && button.getAttribute('data-item-index') == index) {
          item.classList.add('is-active');
        }
      } else if (contentType === 'products') {
        const allButtons = item.querySelectorAll('.camp-result__dropdown-button');
        allButtons.forEach(btn => btn.classList.remove('is-active'));
        
        const matchingButton = item.querySelector(`[data-item-index="${index}"]`);
        if (matchingButton) {
          item.classList.add('is-active');
          matchingButton.classList.add('is-active');
          
          const summary = item.querySelector('.camp-result__dropdown-summary .camp-result__dropdown-label');
          if (summary) {
            summary.textContent = matchingButton.textContent;
          }
        }
      }
    });

    // Get content data from JSON using indices
    const contentData = this.getContentDataByButton(button);
    if (!contentData) return;

    // Render content based on type
    if (contentData.type === 'text') {
      contentContainer.innerHTML = `
        <h3 class="camp-result__content-title heading-small">${contentData.blockTitle}</h3>
        <div class="camp-result__content-text">${contentData.text}</div>
      `;
    } else if (contentData.type === 'featured_products') {
      contentContainer.innerHTML = `
        <h3 class="camp-result__content-title heading-small">${contentData.blockTitle}</h3>
        <div class="camp-result__content-featured-products is-loading" data-item-title="${contentData.blockTitle || ''}"></div>
      `;
      
      // Load featured products using URL fetching
      await this.loadFeaturedProducts(contentContainer.querySelector('.camp-result__content-featured-products'), contentData);
    } else {
      contentContainer.innerHTML = `
        <h3 class="camp-result__content-title heading-small">${contentData.blockTitle}</h3>
        <h4 class="camp-result__content-subtitle">Select ${contentData.itemTitle}</h4>
        <div class="camp-result__content-products is-loading" data-enable-dept-filtering="${contentData.enableDepartmentFiltering || false}" data-item-title="${contentData.itemTitle || ''}" data-camp-category="${index}"></div>
      `;
      
      const productsContainer = contentContainer.querySelector('.camp-result__content-products');
      const filterParams = this.pendingFilterParams || '';
      this.pendingFilterParams = null;
      await this.loadProducts(contentData, productsContainer, filterParams);
    }
  }

  /**
   * Get content data from JSON using button indices
   */
  getContentDataByButton(button) {
    const blockIndex = parseInt(button.getAttribute('data-block-index'));
    const itemSubIndex = parseInt(button.getAttribute('data-item-sub-index'));
    const campData = window.RsCampPage.getCampData();
    const block = campData.checklist_blocks.find(b => b.index === blockIndex);

    if (!block) return null;

    if (block.checklist_content_type === 'text') {
      return {
        type: 'text',
        blockTitle: block.checklist_title,
        text: block.checklist_text
      };
    } else if (block.checklist_content_type === 'featured_products') {
      return {
        type: 'featured_products',
        blockTitle: block.checklist_title
      };
    } else {
      const item = block.checklist_items.find(i => i.index === itemSubIndex);
      if (!item) return null;

      return {
        type: 'products',
        blockTitle: block.checklist_title,
        itemTitle: item.title,
        collectionUrl: item.collection,
        enableFiltering: item.enable_filtering,
        enableDepartmentFiltering: item.enable_department_filtering
      };
    }
  }

  /**
   * Load products using global loadProducts function
   * @param {Object} contentData - Content metadata
   * @param {HTMLElement} container - Products container
   * @param {string} [filterParams=''] - Optional filter/sort/page params for restore
   */
  async loadProducts(contentData, container, filterParams = '') {
    await window.RsCampPage.loadProducts(
      contentData.collectionUrl,
      filterParams,
      container,
      contentData.enableFiltering,
      contentData.enableDepartmentFiltering
    );

    // Initialize line item properties for dynamically loaded products
    this.initializeDynamicProducts(container, contentData);
  }

  /**
   * Load featured products by fetching URL and extracting main-content
   */
  async loadFeaturedProducts(container, contentData) {
    container.classList.add('is-loading');
    
    try {
      const url = `${location.pathname}?view=rs-camp-featured-products`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Try multiple selectors to find the main content
      let mainContent = tempDiv.querySelector('#MainContent') || tempDiv.querySelector('.main-content');
      
        if (mainContent) {
          container.innerHTML = mainContent.innerHTML;
          
          // Add line item properties to featured products
          this.initializeFeaturedProducts(container, contentData);
        } else {
          throw new Error('main content area not found in response');
        }
      
    } catch (error) {
      console.error('Error loading featured products:', error);
      container.innerHTML = '<p>Something went wrong. Please try again.</p>';
    } finally {
      container.classList.remove('is-loading');
    }
  }

  /**
   * Initialize line item properties for dynamically loaded products
   */
  initializeDynamicProducts(container, contentData) {
    window.RsCampPage.addLineItemPropertiesToContainer(container, contentData.itemTitle);
  }

  /**
   * Initialize line item properties for featured products
   */
  initializeFeaturedProducts(container, contentData) {
    window.RsCampPage.addLineItemPropertiesToContainer(container, contentData.blockTitle);
    this.prefillFeaturedProductsNameInputs(container);
  }

  /**
   * Prefill name inputs in featured products with camper's name from cookie
   */
  prefillFeaturedProductsNameInputs(container) {
    const formData = window.RsCampPage.getCookie();
    
    if (!formData || !formData.camperName) return;

    const nameInputs = container.querySelectorAll('.properties__input[name="properties[Name]"]');
    nameInputs.forEach(input => {
      if (input && !input.value) {
        input.value = formData.camperName;
      }
    });
  }

  /**
   * Handle quick-add modal created event (for multi-variant products)
   */
  handleQuickAddModalCreated(event) {
    const { modal, triggerButton } = event.detail;
    
    if (!triggerButton) return;
    
    const options = {
      campTitle: triggerButton.getAttribute('data-camp-title'),
      campCategory: triggerButton.getAttribute('data-camp-category'),
      camperName: triggerButton.getAttribute('data-camper-name'),
      campNameLabel: triggerButton.getAttribute('data-camp-name-label') || '',
      camperAge: triggerButton.getAttribute('data-camper-age'),
      camperHeight: triggerButton.getAttribute('data-camper-height'),
      camperWeight: triggerButton.getAttribute('data-camper-weight')
    };
    
    if (!options.campTitle || !options.campCategory || !options.camperName) return;
    
    modal.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      window.RsCampPage.addLineItemPropertiesToForm(form, options);
    });
  }

  /**
   * Setup navigation persistence: save scroll when navigating to product pages
   */
  setupNavigationPersistence() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (link && link.hostname === location.hostname && !link.target) {
        window.RsCampPage.saveCampScrollPosition();
        window.RsCampPage.storeCampContextForProductUrl(link);
      }
    }, true);
  }

  /**
   * Add event listeners to slider arrows to close open dropdowns
   */
  addSliderArrowListeners(gridSlider) {
    const arrowButtons = gridSlider.querySelectorAll('[data-button-arrow]');
    arrowButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeAllDropdowns();
      });
    });
  }

  /**
   * Close all open dropdowns
   */
  closeAllDropdowns() {
    const openDropdowns = this.querySelectorAll('.camp-result__dropdown-details[open]');
    openDropdowns.forEach(dropdown => {
      dropdown.removeAttribute('open');
    });
  }

  /**
   * Reset all dropdown titles to their original state
   */
  resetDropdownTitles() {
    const dropdownLabels = this.querySelectorAll('.camp-result__dropdown-label[data-original-title]');
    dropdownLabels.forEach(label => {
      const originalTitle = label.getAttribute('data-original-title');
      if (originalTitle) {
        label.textContent = originalTitle;
      }
    });
  }

  /**
   * Scroll to content area on mobile devices
   */
  scrollToContentOnMobile() {
    // Only scroll on mobile devices
    if (window.innerWidth >= 750) return;
    
    const contentArea = this.querySelector('[data-camp-result-content]');
    if (!contentArea) return;
    
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 120;
    const offset = headerHeight + 60;

    runAfterNextPaint(() => {
      const elementTop = contentArea.getBoundingClientRect().top + window.scrollY;
      const targetY = Math.max(0, elementTop - offset);
      window.scrollTo(0, targetY);
    });
  }

  /**
   * Generate dropdown HTML for mobile slider navigation
   */
  generateDropdownHTML() {
    const campData = window.RsCampPage.getCampData();
    const blocks = campData.checklist_blocks;
    const dropdownItems = [];
    let globalIndex = 1;
    
    blocks.forEach((block, blockIndex) => {
      if (block.checklist_content_type === 'text') {
        dropdownItems.push(`
          <div class="camp-result__dropdown-item grid-item" data-grid-item data-content-type="text">
            <div class="camp-result__dropdown-content">
              <button type="button" class="btn btn--full" data-item-index="${globalIndex}" data-block-index="${block.index}">
                ${block.checklist_title}
              </button>
            </div>
          </div>
        `);
        globalIndex++;
      } else if (block.checklist_content_type === 'featured_products') {
        dropdownItems.push(`
          <div class="camp-result__dropdown-item grid-item" data-grid-item data-content-type="featured_products">
            <div class="camp-result__dropdown-content">
              <button type="button" class="btn btn--full" data-item-index="${globalIndex}" data-block-index="${block.index}">
                ${block.checklist_title}
              </button>
            </div>
          </div>
        `);
        globalIndex++;
      } else {
        const buttonsHTML = block.checklist_items.map(item => {
          const buttonHTML = `
            <button type="button" class="camp-result__dropdown-button btn btn--outline btn--small" data-item-index="${globalIndex}" data-block-index="${block.index}" data-item-sub-index="${item.index}">
              ${item.title}
            </button>
          `;
          globalIndex++;
          return buttonHTML;
        }).join('');

        dropdownItems.push(`
          <div class="camp-result__dropdown-item grid-item" data-grid-item data-content-type="products">
            <details class="camp-result__dropdown-details" data-collapsible>
              <summary class="camp-result__dropdown-summary" data-collapsible-trigger>
                <span class="camp-result__dropdown-label" data-original-title="${block.checklist_title}">${block.checklist_title}</span>
                <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" viewBox="0 0 24 24"><path d="M6 12h6m6 0h-6m0 0V6m0 6v6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" viewBox="0 0 24 24"><path d="M6 12h12" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </summary>
              <div class="camp-result__dropdown-body" data-collapsible-body>
                <div class="camp-result__dropdown-content" data-collapsible-content>
                  ${buttonsHTML}
                </div>
              </div>
            </details>
          </div>
        `);
      }
    });
    
    return dropdownItems.join('');
  }

  /**
   * Generate accordion HTML for desktop sidebar
   */
  generateAccordionHTML() {
    const campData = window.RsCampPage.getCampData();
    const blocks = campData.checklist_blocks;
    let globalIndex = 1;
    
    const sidebarHTML = blocks.map((block, blockIndex) => {
      if (block.checklist_content_type === 'text') {
        const buttonHTML = `
          <button type="button" class="camp-result__nav-button" data-item-index="${globalIndex}" data-block-index="${block.index}">
            ${block.checklist_title}
          </button>
        `;
        globalIndex++;
        return buttonHTML;
      } else if (block.checklist_content_type === 'featured_products') {
        const buttonHTML = `
          <button type="button" class="camp-result__nav-button" data-item-index="${globalIndex}" data-block-index="${block.index}">
            ${block.checklist_title}
          </button>
        `;
        globalIndex++;
        return buttonHTML;
      } else {
        const isFirst = blockIndex === 0;
        const openAttribute = isFirst ? 'open="true"' : '';
        const openStyle = isFirst ? 'style="height: auto;"' : '';

        const itemsHTML = block.checklist_items.map(item => {
          const buttonHTML = `
            <button type="button" class="camp-result__nav-button" data-item-index="${globalIndex}" data-block-index="${block.index}" data-item-sub-index="${item.index}">
              ${item.title}
            </button>
          `;
          globalIndex++;
          return buttonHTML;
        }).join('');

        return `
          <details class="camp-result__accordion" data-collapsible ${openAttribute}>
            <summary class="camp-result__accordion-title" data-collapsible-trigger>
              <span>${block.checklist_title}</span>
              <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" viewBox="0 0 24 24"><path d="M6 12h6m6 0h-6m0 0V6m0 6v6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" viewBox="0 0 24 24"><path d="M6 12h12" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </summary>
            <div class="camp-result__accordion-body" data-collapsible-body ${openStyle}>
              <div class="camp-result__accordion-content" data-collapsible-content>
                ${itemsHTML}
              </div>
            </div>
          </details>
        `;
      }
    }).join('');

    return `
      <collapsible-elements single="true">
        <div class="camp-result__accordion-wrapper">
          ${sidebarHTML}
        </div>
      </collapsible-elements>
    `;
  }

  /**
   * Navigate to specific block by index
   */
  navigateToBlock(index) {
    this.currentIndex = index;
    // TODO: Update content area and highlight current block
  }

  /**
   * Navigate to previous block
   */
  navigatePrevious() {
    if (this.currentIndex > 0) {
      this.navigateToBlock(this.currentIndex - 1);
    }
  }

  /**
   * Navigate to next block
   */
  navigateNext() {
    const campData = window.RsCampPage.getCampData();
    if (campData && this.currentIndex < campData.checklist_blocks.length - 1) {
      this.navigateToBlock(this.currentIndex + 1);
    }
  }
}


/**
 * Custom element for the Filter and Sort button - toggles the filter/sort panel on mobile
 */
class RSCampResultFilterAndSortButton extends HTMLElement {
  constructor() {
    super();
    this.boundHandleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('button');
    if (this.button) {
      this.button.addEventListener('click', this.boundHandleClick);
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.boundHandleClick);
    }
  }

  handleClick() {
    const container = this.closest('.camp-result__content-products');
    if (!container) return;

    const panel = container.querySelector('rs-camp-result-filter-and-sort');
    if (!panel) return;

    const isOpen = panel.getAttribute('data-is-open') === 'true';
    const willOpen = !isOpen;
    panel.setAttribute('data-is-open', willOpen.toString());

    if (willOpen) {
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    } else {
      panel.closePanel?.();
    }
  }
}

/**
 * Custom element for handling filters and sort
 */
class RSCampResultFilterAndSort extends HTMLElement {
  constructor() {
    super();
    this.boundHandleFilterChange = this.handleFilterChange.bind(this);
    this.boundHandleOverlayClick = this.handleOverlayClick.bind(this);
  }

  connectedCallback() {
    const filterSelects = this.querySelectorAll('select');
    filterSelects.forEach(select => {
      select.addEventListener('change', this.boundHandleFilterChange);
    });
    this.addEventListener('click', this.boundHandleOverlayClick);
  }

  disconnectedCallback() {
    const filterSelects = this.querySelectorAll('select');
    filterSelects.forEach(select => {
      select.removeEventListener('change', this.boundHandleFilterChange);
    });
    this.removeEventListener('click', this.boundHandleOverlayClick);
  }

  /**
   * Close overlay when clicking the backdrop (mobile only)
   */
  handleOverlayClick(e) {
    if (!window.theme?.isMobile?.()) return;
    if (this.getAttribute('data-is-open') !== 'true') return;

    const contentPanel = this.querySelector('.camp-result__filter-and-sort');
    if (contentPanel?.contains(e.target)) return;

    this.closePanel();
  }

  /**
   * Close panel with animation
   */
  closePanel() {
    if (this.getAttribute('data-is-closing') === 'true') return;

    this.setAttribute('data-is-closing', 'true');
    const contentPanel = this.querySelector('.camp-result__filter-and-sort');

    const onAnimationEnd = () => {
      this.removeAttribute('data-is-closing');
      this.setAttribute('data-is-open', 'false');
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    };

    if (window.theme?.waitForAnimationEnd && contentPanel) {
      window.theme.waitForAnimationEnd(contentPanel).then(onAnimationEnd);
    } else {
      setTimeout(onAnimationEnd, 500);
    }
  }

  /**
   * Handle filter or sort change and update products
   */
  async handleFilterChange() {
    const container = this.closest('.camp-result__content-products');
    if (!container) return;

    const collectionUrl = this.getAttribute('data-collection-url');
    if (!collectionUrl) return;

    // Build filter and sort query params (includes sort_by from sort select)
    const filterParams = this.buildFilterParams();
    
    // Check if department filtering is enabled for current content
    const enableDeptFiltering = container.getAttribute('data-enable-dept-filtering') === 'true';
    
    // Load products with filters and sort
    await window.RsCampPage.loadProducts(collectionUrl, filterParams, container, true, enableDeptFiltering);

    // Update history state for back-button persistence
    const category = container.getAttribute('data-camp-category');
    const params = new URLSearchParams(filterParams);
    const sortBy = params.get('sort_by') || '';
    params.delete('sort_by');
    params.delete('page');
    window.RsCampPage.updateCampStateInHistory({
      category: category ? parseInt(category, 10) : null,
      page: 1,
      sortBy: sortBy || null,
      filterParams: params.toString()
    });

    // Panel is replaced by loadProducts - unlock scroll immediately
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));

    // Scroll to the result container
    window.RsCampPage.scrollToResult();
  }

  /**
   * Build filter and sort query parameters from select boxes
   */
  buildFilterParams() {
    const filterSelects = this.querySelectorAll('select');
    const params = [];

    filterSelects.forEach(select => {
      const name = select.getAttribute('name');
      const value = select.value;
      
      if (name && value && value.trim() !== '') {
        params.push(`${name}=${encodeURIComponent(value)}`);
      }
    });

    return params.join('&');
  }
}

/**
 * Custom element for handling reset filters
 */
class RSCampResultResetFilters extends HTMLElement {
  constructor() {
    super();
    this.boundHandleResetFilters = this.handleResetFilters.bind(this);
  }

  connectedCallback() {
    this.resetFiltersButton = this.querySelector('button');
    if (this.resetFiltersButton) {
      this.resetFiltersButton.addEventListener('click', this.boundHandleResetFilters);
    }
  }

  disconnectedCallback() {
    if (this.resetFiltersButton) {
      this.resetFiltersButton.removeEventListener('click', this.boundHandleResetFilters);
    }
  }

  /**
   * Handle reset filters button click
   */
  async handleResetFilters() {
    const container = this.closest('.camp-result__content-products');
    if (!container) return;

    const collectionUrl = this.getAttribute('data-collection-url');
    if (!collectionUrl) return;

    // Reset all filter and sort select boxes to default value
    const filtersElement = container.querySelector('rs-camp-result-filter-and-sort');
    if (filtersElement) {
      const filterSelects = filtersElement.querySelectorAll('select');
      filterSelects.forEach(select => {
        select.selectedIndex = 0;
      });
    }

    // Check if department filtering is enabled for current content
    const enableDeptFiltering = container.getAttribute('data-enable-dept-filtering') === 'true';

    // Reload products without user filters but keep department filter if enabled
    await window.RsCampPage.loadProducts(collectionUrl, '', container, !!filtersElement, enableDeptFiltering);

    // Update history state - clear filter/sort/page
    const category = container.getAttribute('data-camp-category');
    window.RsCampPage.updateCampStateInHistory({
      category: category ? parseInt(category, 10) : null,
      page: 1,
      sortBy: null,
      filterParams: ''
    });
  }
}

/**
 * Custom element for handling pagination
 */
class RSCampResultPagination extends HTMLElement {
  constructor() {
    super();
    this.boundHandlePaginationClick = this.handlePaginationClick.bind(this);
  }

  connectedCallback() {
    const paginationLinks = this.querySelectorAll('a[data-page]');
    paginationLinks.forEach(link => {
      link.addEventListener('click', this.boundHandlePaginationClick);
    });
  }

  disconnectedCallback() {
    const paginationLinks = this.querySelectorAll('a[data-page]');
    paginationLinks.forEach(link => {
      link.removeEventListener('click', this.boundHandlePaginationClick);
    });
  }

  /**
   * Handle pagination link click
   */
  async handlePaginationClick(event) {
    event.preventDefault();
    
    const container = this.closest('.camp-result__content-products');
    if (!container) return;

    const collectionUrl = this.getAttribute('data-collection-url');
    if (!collectionUrl) return;

    const pageNumber = event.target.getAttribute('data-page');
    if (!pageNumber) return;

    // Build pagination params
    const paginationParams = `page=${pageNumber}`;
    
    // Get existing filter and sort params if filters exist
    const filtersElement = container.querySelector('rs-camp-result-filter-and-sort');
    let filterParams = '';
    if (filtersElement && typeof filtersElement.buildFilterParams === 'function') {
      filterParams = filtersElement.buildFilterParams();
    }

    // Combine filter and pagination params
    let allParams = paginationParams;
    if (filterParams) {
      allParams = `${filterParams}&${paginationParams}`;
    }

    // Check if department filtering is enabled for current content
    const enableDeptFiltering = container.getAttribute('data-enable-dept-filtering') === 'true';

    // Load products with pagination and filters
    await window.RsCampPage.loadProducts(collectionUrl, allParams, container, !!filtersElement, enableDeptFiltering);

    // Update history state for back-button persistence
    const category = container.getAttribute('data-camp-category');
    const params = new URLSearchParams(allParams);
    const sortBy = params.get('sort_by') || '';
    params.delete('sort_by');
    window.RsCampPage.updateCampStateInHistory({
      category: category ? parseInt(category, 10) : null,
      page: parseInt(pageNumber, 10),
      sortBy: sortBy || null,
      filterParams: params.toString()
    });

    window.RsCampPage.scrollToResult();
  }
}

/**
 * RS Camp Result Summary. Handles summary section:
 *  - Displays summary of selected items
 */
class RSCampResultSummary extends HTMLElement {
  constructor() {
    super();

    this.viewCartButtons = document.querySelectorAll('[data-camp-summary-view-cart]');
    this.viewCartCounts = document.querySelectorAll('[data-camp-summary-view-cart-count]');
    this.cartDrawer = document.querySelector('cart-drawer');

    this.boundViewCart = this.handleViewCart.bind(this);
    this.boundCartChange = this.handleCartChange.bind(this);
  }

  connectedCallback() {
    this.viewCartButtons.forEach(button => {
      button.addEventListener('click', this.boundViewCart);
    });
    document.addEventListener('theme:cart:change', this.boundCartChange);

    // Initial cart summary update
    this.updateCartSummary();
  }
  
  disconnectedCallback() {
    this.viewCartButtons.forEach(button => {
      button.removeEventListener('click', this.boundViewCart);
    });
    document.removeEventListener('theme:cart:change', this.boundCartChange);
  }

  /**
   * Handle view cart button click
   */
  handleViewCart(event) {
    const button = event.target;
    if (!button || button.disabled) return;
    
    if (this.cartDrawer) {
      this.cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
    } else {
      window.location = theme.routes.cart_url;
    }
  }

  /**
   * Handle cart change event
   */
  handleCartChange() {
    this.updateCartSummary();
  }

  /**
   * Update cart summary
   */
  async updateCartSummary() {
    try {
      const cartData = await this.fetchCartData();
      const categorySummary = this.groupItemsByCategory(cartData.items);
      this.renderCartSummary(categorySummary);
    } catch (error) {
      console.error('Error updating cart summary:', error);
    }
  }

  /**
   * Group cart items by category and calculate quantities (only for current camper)
   */
  groupItemsByCategory(cartItems) {
    const categoryMap = new Map();
    const formData = window.RsCampPage.getCookie();
    const campData = window.RsCampPage.getCampData();

    if (!formData || !campData) return [];

    cartItems.forEach(item => {
      // Check if item matches current form data
      const itemCampTitle = item.properties && item.properties._CampTitle;
      const itemCamperName = item.properties && item.properties._CamperName;
      const itemCampCategory = item.properties && item.properties._CampCategory;
      const itemCamperAge = item.properties && item.properties._CamperAge;
      const itemCamperHeight = item.properties && item.properties._CamperHeight;
      const itemCamperWeight = item.properties && item.properties._CamperWeight;

      // Only include items that match current camp and camper details
      const matchesCamper = 
        itemCampTitle === campData.camp_name && 
        itemCamperName === formData.camperName &&
        String(itemCamperAge) === String(formData.camperAge) &&
        String(itemCamperHeight) === String(formData.camperHeight) &&
        String(itemCamperWeight) === String(formData.camperWeight);

      if (matchesCamper && itemCampCategory) {
        if (categoryMap.has(itemCampCategory)) {
          categoryMap.set(itemCampCategory, categoryMap.get(itemCampCategory) + item.quantity);
        } else {
          categoryMap.set(itemCampCategory, item.quantity);
        }
      }
    });

    // Convert map to array of objects
    return Array.from(categoryMap.entries()).map(([category, quantity]) => ({
      category,
      quantity
    }));
  }

  /**
   * Render cart summary in the summary content area
   */
  renderCartSummary(categorySummary) {
    const summaryContent = document.querySelector('[data-camp-summary-content]');
    const summaryViewCartButtons = document.querySelectorAll('[data-camp-summary-view-cart]');
    const summaryViewCartCounts = document.querySelectorAll('[data-camp-summary-view-cart-count]');

    if (!summaryContent) return;

    if (categorySummary.length === 0) {
      const campData = window.RsCampPage.getCampData();
      summaryContent.innerHTML = campData.summary_default_content;
      
      summaryViewCartButtons.forEach(button => {
        button.disabled = true;
      });
    } else {
      const summaryHTML = categorySummary
        .map(({ category, quantity }) => `
          <div class="camp-result__summary--item">
            <span class="camp-result__summary--quantity">${quantity}</span>
            <span class="camp-result__summary--symbol">x</span>
            <span class="camp-result__summary--category">${category}</span>      
          </div>
        `)
        .join('');

      summaryContent.innerHTML = summaryHTML;
      
      summaryViewCartButtons.forEach(button => {
        button.disabled = false;
      });
      
      // Add scroll indicator if content exceeds height
      this.addScrollIndicator(summaryContent);
    }

    const totalQuantity = categorySummary.reduce((acc, { quantity }) => acc + quantity, 0);
    summaryViewCartCounts.forEach(countElement => {
      countElement.innerHTML = `(${totalQuantity})`;
    });
  }

  /**
   * Add scroll indicator if content exceeds height limit
   */
  addScrollIndicator(summaryContent) {
    // Remove existing scroll indicator
    const existingIndicator = summaryContent.querySelector('.camp-result__summary--scroll-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }
    
    // Check if content height exceeds the limit (200px)
    if (summaryContent.scrollHeight > 200) {
      const scrollIndicator = document.createElement('div');
      scrollIndicator.className = 'camp-result__summary--scroll-indicator is-active';
      scrollIndicator.innerHTML = `
        <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-arrow-up" viewBox="0 0 24 24"><path d="M12.25 18.5V6m0 0 6 6m-6-6-6 6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <span>Scroll for more items</span>`;
    
      summaryContent.appendChild(scrollIndicator);
      
      const handleScroll = () => {
        if (summaryContent.scrollTop > 10) {
          scrollIndicator.classList.remove('is-active');
        } else {
          scrollIndicator.classList.add('is-active');
        }
      };
      
      summaryContent.addEventListener('scroll', handleScroll);
      scrollIndicator._handleScroll = handleScroll;
    }
  }

  /**
   * Fetch cart data from Shopify
   */
  async fetchCartData() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Failed to fetch cart data: ${response.status}`);
    }
    return response.json();
  }
}

/**
 * REGISTER CUSTOM ELEMENTS
 */
if (!customElements.get('rs-camp-form')) {
  customElements.define('rs-camp-form', RSCampForm);
}
if (!customElements.get('rs-camp-result')) {
  customElements.define('rs-camp-result', RSCampResult);
}
if (!customElements.get('rs-camp-result-filter-and-sort-button')) {
  customElements.define('rs-camp-result-filter-and-sort-button', RSCampResultFilterAndSortButton);
}
if (!customElements.get('rs-camp-result-filter-and-sort')) {
  customElements.define('rs-camp-result-filter-and-sort', RSCampResultFilterAndSort);
}
if (!customElements.get('rs-camp-result-reset-filters')) {
  customElements.define('rs-camp-result-reset-filters', RSCampResultResetFilters);
}
if (!customElements.get('rs-camp-result-pagination')) {
  customElements.define('rs-camp-result-pagination', RSCampResultPagination);
}
if (!customElements.get('rs-camp-result-summary')) {
  customElements.define('rs-camp-result-summary', RSCampResultSummary);
}
if (!customElements.get('rs-camp-result-reset-filters')) {
  customElements.define('rs-camp-result-reset-filters', RSCampResultResetFilters);
}

function runCampPdpLineItemInit() {
  if (typeof window.RsCampPage?.applyStoredCampLineItemPropertiesToPdp === 'function') {
    window.RsCampPage.applyStoredCampLineItemPropertiesToPdp();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runCampPdpLineItemInit);
} else {
  runCampPdpLineItemInit();
}